/*
  Bean & Paw native motion (taste audit 2026-10-24, SHOP-TASTE-AUDIT.md "Motion plan"; no library).
  Store rule: <= 400ms, transform/opacity only, CLS 0, never on the LCP element, nothing under reduced motion.
  1. Cart-count "added" pop: Horizon fires `cart:update` (ThemeEvents.cartUpdate) after any add/change; the header
     icon's cart bubble gets .bp-pop once (a 280ms scale in CSS), and the open cart drawer's badge pops after the
     drawer has finished sliding in. Bubbles may be re-rendered, so they are looked up after the update has painted.
  2. Spotlight on product cards (fine pointer only): the pointer position inside a card image is written to
     --bp-spot-x / --bp-spot-y on that image box; CSS moves a fixed-size soft highlight with transform. rAF-throttled,
     one listener for the page.
*/
const reduce = matchMedia('(prefers-reduced-motion: reduce)');

const DRAWER_BUBBLE = '.cart-drawer__heading .cart-bubble';

function pop(bubble) {
  if (!bubble || bubble.classList.contains('visually-hidden')) return;
  bubble.classList.remove('bp-pop');
  void bubble.offsetWidth; // restart the animation if it is still running
  bubble.classList.add('bp-pop');
  bubble.addEventListener('animationend', () => bubble.classList.remove('bp-pop'), { once: true });
}

// Horizon renders the cart drawer inside the header, so one cart:update reaches two bubbles: the header icon and the
// drawer's "Cart (n)" badge. The icon pops at once. The badge waits until the drawer's open animation has finished
// (it would otherwise play while the drawer is still sliding in), with a 600ms cap; it's looked up again after the
// wait because the drawer re-renders its contents on cart updates.
async function popCartBubble() {
  if (reduce.matches) return;
  await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  for (const bubble of document.querySelectorAll('header .cart-bubble')) {
    if (!bubble.closest('.cart-drawer__heading')) pop(bubble);
  }
  const dialog = document.querySelector('dialog.cart-drawer__dialog');
  if (!dialog) return;
  // the drawer may open a frame or two after the event (auto-open), so give it up to 600ms before deciding it won't
  const start = performance.now();
  while (!dialog.open && performance.now() - start < 600) await new Promise((resolve) => requestAnimationFrame(resolve));
  if (!dialog.open) return;
  const opening = dialog.getAnimations().map((a) => a.finished.catch(() => {}));
  await Promise.race([Promise.all(opening), new Promise((resolve) => setTimeout(resolve, 600))]);
  pop(dialog.querySelector(DRAWER_BUBBLE));
}

document.addEventListener('cart:update', popCartBubble);

const fine = matchMedia('(hover: hover) and (pointer: fine)');
const SPOT = 'product-card .product-media, .bp-card__media'; // the image boxes that carry the highlight
let frame = 0;
let last = null;

function onMove(e) {
  last = e;
  if (frame) return;
  frame = requestAnimationFrame(() => {
    frame = 0;
    // Horizon cards put an empty overlay link above the image, so resolve the card first, then its image box.
    const card = last.target instanceof Element ? last.target.closest('product-card, .bp-card') : null;
    const box = card?.querySelector(SPOT.split(', ').map((s) => s.replace(/^product-card /, '')).join(', '));
    if (!box) return;
    const r = box.getBoundingClientRect();
    box.style.setProperty('--bp-spot-x', `${Math.round(last.clientX - r.left)}px`);
    box.style.setProperty('--bp-spot-y', `${Math.round(last.clientY - r.top)}px`);
  });
}

if (fine.matches && !reduce.matches) document.addEventListener('pointermove', onMove, { passive: true });
